import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import UploadFolderSVG from "../../../Assets/svg/Folder.svg";
import Spinner from "../../Animations/Spinner/Spinner";
import { FetchAllDepartmentsAction } from "../../../Actions/AdminUser";
import {
  uploadFolder,
  ingestFolder,
} from "../../../Actions/KnowledgeBaseBuilder";
import DropdownField from "./DropdownField";
import { X } from "lucide-react";

class FolderUploader extends Component {
  static propTypes = {
    handleShowAndHideFolderUploader: PropTypes.func.isRequired,
    FetchAllDepartmentsAction: PropTypes.func.isRequired,
    uploadFolder: PropTypes.func.isRequired,
    ingestFolder: PropTypes.func.isRequired,
    departments: PropTypes.array,
    navigate: PropTypes.func.isRequired,
  };

  state = {
    selectedFiles: [],
    folderName: "",
    description: "",
    selectedDepartment: "",
    selectedSubDepartment: "",
    isUploading: false,
    uploadSuccess: false,
    error: null,
    departments: [],
    selectedChatbotOption: "internal Chatbot",
    showConfirmModal: false,
    isCustomerService: false,
  };

  previousBodyOverflow = "";

  componentDidMount() {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    console.log("Component mounting, fetching departments...");
    this.props
      .FetchAllDepartmentsAction()
      .then((result) => {
        console.log("Departments fetched successfully:", result);
        // Extract the departments array from the response
        const depts = result?.departments || result || [];
        this.setState({ departments: Array.isArray(depts) ? depts : [] });
      })
      .catch((error) => {
        console.error("Error fetching departments:", error);
      });
  }

  componentWillUnmount() {
    document.body.style.overflow = this.previousBodyOverflow;
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.departments !== this.props.departments &&
      this.props.departments
    ) {
      console.log("Departments updated in props:", this.props.departments);
      this.setState({ departments: this.props.departments });
    }
  }

  handleFolderChange = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      // Get folder name from the first file's path
      const folderPath = files[0].webkitRelativePath;
      const folderName = folderPath.split("/")[0];

      this.setState({
        selectedFiles: Array.from(files),
        folderName: folderName,
      });
    }
  };

  handleInputChange = (event) => {
    this.setState({ [event.target.name]: event.target.value });
  };

  handleNavigateToCreateDepartment = () => {
    this.props.navigate("/admin-dashboard/create-department");
  };

  handleChatbotOptionChange = (option) => {
    this.setState({ selectedChatbotOption: option });
  };

  handleUpload = () => {
    const {
      selectedFiles,
      folderName,
      description,
      selectedDepartment,
      selectedSubDepartment,
      departments,
      selectedChatbotOption,
    } = this.state;

    if (selectedFiles.length === 0) {
      return;
    }

    // For customer service chatbot, we don't require department selection
    const isCustomerServiceChatbot =
      selectedChatbotOption.toLowerCase() === "customer service chatbot";

    if (
      !isCustomerServiceChatbot &&
      (!selectedDepartment || !selectedSubDepartment)
    ) {
      return;
    }

    // Show confirmation modal for both options, but with different messages
    this.setState({
      showConfirmModal: true,
      isCustomerService: isCustomerServiceChatbot,
    });
  };

  proceedWithUpload = () => {
    const {
      selectedFiles,
      folderName,
      description,
      selectedDepartment,
      selectedSubDepartment,
      departments,
      selectedChatbotOption,
    } = this.state;

    this.setState({ isUploading: true, error: null, showConfirmModal: false });

    // Get department and sub-department details
    const isCustomerServiceChatbot =
      selectedChatbotOption.toLowerCase() === "customer service chatbot";
    const selectedDept = departments?.find(
      (dept) => dept.id.toString() === selectedDepartment
    );
    const selectedSubDept = selectedDept?.sub_departments?.find(
      (subDept) => subDept.id.toString() === selectedSubDepartment
    );

    // Create FormData
    const formData = new FormData();
    formData.append("folder_name", folderName);
    formData.append("description", description);
    formData.append("department_name", selectedDept?.name || "");
    formData.append("department_description", selectedDept?.description || "");
    formData.append("subdepartment_name", selectedSubDept?.name || "");
    formData.append(
      "subdepartment_description",
      selectedSubDept?.description || ""
    );

    // Add is_customer_service flag based on the selected option
    formData.append("is_customer_service", isCustomerServiceChatbot);

    // Append all files
    selectedFiles.forEach((file) => {
      formData.append("files[]", file);
    });

    this.props
      .uploadFolder(formData)
      .then((data) => {
        if (!data.folder_id) {
          throw new Error("No folder ID received from upload");
        }
        return this.props.ingestFolder(data.folder_id);
      })
      .then(() => {
        this.setState({
          isUploading: false,
          uploadSuccess: true,
        });
        setTimeout(() => {
          this.props.handleShowAndHideFolderUploader();
        }, 3000);
      })
      .catch((error) => {
        console.error("Error during folder upload/ingestion:", error);
        this.setState({
          isUploading: false,
          error: error?.response?.data?.error || error?.message || "Upload failed. Please try again.",
        });
      });
  };

  renderUploadForm = () => {
    const { departments: rawDepartments, selectedChatbotOption } = this.state;
    const {
      folderName,
      description,
      selectedDepartment,
      selectedSubDepartment,
    } = this.state;

    // Normalize departments - handle both array and {departments: array} formats
    const departments = Array.isArray(rawDepartments)
      ? rawDepartments
      : (rawDepartments?.departments || []);

    const isCustomerServiceChatbot =
      selectedChatbotOption.toLowerCase() === "customer service chatbot";

    // Get sub-departments for selected department
    const selectedDept = departments?.find(
      (dept) => dept.id.toString() === selectedDepartment
    );
    const subDepartments = selectedDept?.sub_departments || [];

    return (
      <div className="flex flex-col gap-[1rem] w-full">
        <div className="flex flex-col gap-[0.5rem]">
          <label
            className="text-[#000000] text-[1rem] font-[600]"
            htmlFor="selectedChatbotOption"
          >
            Select Upload Destination
          </label>
          <DropdownField
            id="selectedChatbotOption"
            name="selectedChatbotOption"
            value={selectedChatbotOption}
            onChange={(e) => this.handleChatbotOptionChange(e.target.value)}
            options={[
              { value: "internal Chatbot", label: "Internal Chatbot" },
              {
                value: "customer service chatbot",
                label: "Customer Service Chatbot",
              },
            ]}
          />
        </div>

        {!isCustomerServiceChatbot && (
          <>
            <div className="flex flex-col gap-[0.5rem]">
              <div className="font-[600] text-[1rem]">Folder Name</div>
              <input
                type="text"
                name="folderName"
                value={folderName}
                onChange={this.handleInputChange}
                className="w-full h-[2.5rem] px-[1rem] border-[1px] border-[#D9D9D9] rounded-[0.5rem] outline-none"
                placeholder="Enter folder name"
              />
            </div>

            <div className="flex flex-col gap-[0.5rem]">
              <div className="font-[600] text-[1rem]">Description</div>
              <textarea
                name="description"
                value={description}
                onChange={this.handleInputChange}
                className="w-full h-[6rem] px-[1rem] py-[0.5rem] border-[1px] border-[#D9D9D9] rounded-[0.5rem] outline-none"
                placeholder="Enter description"
              />
            </div>

            <div className="flex flex-col gap-[1rem]">
              <div className="flex flex-col gap-[0.5rem]">
                <div className="font-[600] text-[1rem]">Department</div>
                <DropdownField
                  id="selectedDepartment"
                  name="selectedDepartment"
                  value={selectedDepartment}
                  onChange={(e) => {
                    this.handleInputChange(e);
                    this.setState({ selectedSubDepartment: "" });
                  }}
                  placeholder={
                    Array.isArray(departments) && departments.length > 0
                      ? "Select Department"
                      : "Loading departments..."
                  }
                  disabled={!Array.isArray(departments) || departments.length === 0}
                  options={(Array.isArray(departments) ? departments : []).map(
                    (dept) => ({
                      value: dept.id,
                      label: dept.name || "Unnamed Department",
                    })
                  )}
                />
              </div>

              {selectedDepartment && (
                <>
                  {subDepartments && subDepartments.length > 0 ? (
                    <div className="flex flex-col gap-[0.5rem]">
                      <div className="font-[600] text-[1rem]">
                        Sub-Department
                      </div>
                      <DropdownField
                        id="selectedSubDepartment"
                        name="selectedSubDepartment"
                        value={selectedSubDepartment}
                        onChange={this.handleInputChange}
                        placeholder="Select Sub-Department"
                        options={subDepartments.map((subDept) => ({
                          value: subDept.id,
                          label: subDept.name || "Unnamed Sub-Department",
                        }))}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-[1rem] items-center p-4 bg-[#FEF3F2] rounded-lg border border-[#FEE4E2]">
                      <p className="text-[#B42318] text-center">
                        No sub-departments available for this department. Please
                        create a sub-department first.
                      </p>
                      <button
                        onClick={this.handleNavigateToCreateDepartment}
                        className="px-4 py-2 bg-primary-blue text-white rounded-md hover:bg-primary-blue-dark transition-colors"
                      >
                        Create Sub-Department
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Folder Upload Section */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col h-44 items-center justify-center text-sm gap-4 border border-primary-blue-light border-dashed bg-[#E7F1FF] rounded-lg">
            <img src={UploadFolderSVG} alt="Upload" className="w-16 h-16" />
            <div className="flex flex-col gap-2 items-center">
              <p>
                Drag & drop your folder here or{" "}
                <button
                  type="button"
                  className="border p-2 bg-gray-100"
                  onClick={() => this.folderInput.click()}
                >
                  choose folder
                </button>
                <input
                  type="file"
                  webkitdirectory="true"
                  mozdirectory="true"
                  directory="true"
                  style={{ display: "none" }}
                  ref={(input) => (this.folderInput = input)}
                  onChange={this.handleFolderChange}
                />
              </p>
              {this.state.selectedFiles.length > 0 && (
                <p className="text-green-600">
                  Selected Folder: {this.state.folderName}
                </p>
              )}
            </div>
          </div>

          {/* Upload/Ingest Button */}
          <button
            className={`py-3 font-SFPro font-bold text-base text-white rounded-full ${
              !isCustomerServiceChatbot &&
              (!selectedDepartment ||
                (selectedDepartment && subDepartments.length === 0))
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-primary-blue"
            }`}
            onClick={this.handleUpload}
            disabled={
              !this.state.selectedFiles.length ||
              (!isCustomerServiceChatbot &&
                (!selectedDepartment ||
                  (selectedDepartment && subDepartments.length === 0)))
            }
          >
            Upload Folder
          </button>
        </div>
      </div>
    );
  };

  renderLoadingState = () => {
    return (
      <div className="flex flex-col items-center justify-center gap-8 py-12">
        <Spinner />
        <p className="text-xl font-semibold">Uploading your folder...</p>
      </div>
    );
  };

  renderSuccessState = () => {
    return (
      <div className="flex flex-col items-center justify-center gap-8 py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Upload Successful!
          </h3>
          <p className="text-gray-600">
            Your folder has been successfully uploaded.
          </p>
        </div>
      </div>
    );
  };

  renderConfirmationModal = () => {
    const { isCustomerService } = this.state;

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full">
          <h3 className="text-xl font-bold mb-4">Confirmation Required</h3>
          <p className="mb-6">
            {isCustomerService
              ? "Are you sure you want to upload this folder to the customer-facing chatbot? This content will be publicly accessible to customers."
              : "Are you sure you want to upload this folder?"}
          </p>
          <div className="flex justify-end gap-4">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              onClick={() => this.setState({ showConfirmModal: false })}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-primary-blue text-white rounded hover:bg-blue-700"
              onClick={this.proceedWithUpload}
            >
              Confirm Upload
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { handleShowAndHideFolderUploader } = this.props;
    const { isUploading, uploadSuccess, error, showConfirmModal } = this.state;

    return (
      <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-40 z-50">
        <div className="flex flex-col gap-4 w-full max-w-2xl max-h-[90vh] bg-white px-10 py-8 mx-4 rounded-lg overflow-y-auto">
          {/* Close Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleShowAndHideFolderUploader}
              aria-label="Close uploader"
            >
              <X size={18} strokeWidth={2.25} />
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {isUploading
            ? this.renderLoadingState()
            : uploadSuccess
            ? this.renderSuccessState()
            : this.renderUploadForm()}

          {showConfirmModal && this.renderConfirmationModal()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    departments: state.adminUser?.departments || [],
  };
};

export default withRouterHooks(
  connect(mapStateToProps, {
    FetchAllDepartmentsAction,
    uploadFolder,
    ingestFolder,
  })(FolderUploader)
);
